/**
 * ReadinessHub — aggregated tile that summarises publish readiness across
 * exams, quizzes, assignments, timetable, campaigns and student profiles.
 *
 * Hits `GET /api/school/workflow/dashboard/{tenant_id}` and renders a
 * compact, link-able overview so admins can spot what's blocking go-live
 * across the whole school in one glance. Drill-downs reuse the per-entity
 * readiness pages.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, CheckCircle2, Rocket, ChevronRight } from 'lucide-react';
import api from '@/lib/api';

const MODULE_LABELS = {
  online_exams_drafts: 'Online exams (drafts)',
  quizzes_drafts: 'Quizzes (drafts)',
  assignments_drafts: 'Assignments (drafts)',
  timetable: 'Timetable',
  campaigns_drafts: 'Campaigns (drafts)',
  student_profiles: 'Student profiles',
};

const pctColor = (p) => {
  if (p >= 100) return 'text-emerald-600';
  if (p >= 70) return 'text-amber-600';
  return 'text-rose-600';
};

const barColor = (p) => {
  if (p >= 100) return 'bg-emerald-500';
  if (p >= 70) return 'bg-amber-500';
  return 'bg-rose-500';
};

/**
 * Renders one module section. List modules (exams/quizzes/etc.) show their
 * top blocker so the admin knows what to fix; single-row modules (timetable)
 * just show the overall progress.
 */
const ModuleRow = ({ module, onItemClick }) => {
  // Single-row modules: timetable, student_profiles
  if (module.module === 'timetable') {
    const pct = module.completion ?? 0;
    return (
      <div className="py-2 border-b last:border-0" data-testid="readiness-row-timetable">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-slate-700">{module.label}</span>
          <span className={`text-sm font-semibold ${pctColor(pct)}`}>{pct}%</span>
        </div>
        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full ${barColor(pct)}`} style={{ width: `${pct}%` }} />
        </div>
        {!module.can_publish && (module.blockers || []).length > 0 && (
          <p className="text-xs text-rose-600 mt-1 line-clamp-1">{module.blockers[0]}</p>
        )}
      </div>
    );
  }

  if (module.module === 'student_profiles') {
    const inc = module.incomplete ?? 0;
    const total = module.count ?? 0;
    const pct = total ? Math.round(100 * (total - inc) / total) : 100;
    return (
      <div className="py-2 border-b last:border-0" data-testid="readiness-row-student-profiles">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-slate-700">{module.label}</span>
          <span className={`text-sm font-semibold ${pctColor(pct)}`}>
            {total - inc}/{total} complete
          </span>
        </div>
        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full ${barColor(pct)}`} style={{ width: `${pct}%` }} />
        </div>
        {inc > 0 && <p className="text-xs text-amber-600 mt-1">{inc} student(s) below 70% complete.</p>}
      </div>
    );
  }

  // List modules — exams/quizzes/assignments/campaigns
  const items = module.items || [];
  if (items.length === 0) {
    return (
      <div className="py-2 border-b last:border-0" data-testid={`readiness-row-${module.module}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">{module.label}</span>
          <Badge variant="outline" className="text-[10px] py-0 px-1.5 text-emerald-700 border-emerald-200 bg-emerald-50">
            All published
          </Badge>
        </div>
      </div>
    );
  }
  return (
    <div className="py-2 border-b last:border-0" data-testid={`readiness-row-${module.module}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-slate-700">{module.label}</span>
        <Badge variant="outline" className="text-[10px] py-0 px-1.5">{items.length} pending</Badge>
      </div>
      <ul className="space-y-1">
        {items.slice(0, 3).map((it) => (
          <li
            key={it.id}
            className="flex items-center justify-between text-xs py-0.5 rounded hover:bg-slate-50 -mx-1 px-1 cursor-pointer"
            onClick={() => onItemClick?.(module.module, it)}
            data-testid={`readiness-item-${it.id}`}
          >
            <span className="flex items-center gap-1.5 truncate text-slate-600">
              {it.can_publish
                ? <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
                : <AlertCircle className="h-3 w-3 text-rose-500 shrink-0" />}
              <span className="truncate">{it.name || 'Untitled'}</span>
            </span>
            <span className={`shrink-0 font-medium ${pctColor(it.completion)}`}>{it.completion}%</span>
          </li>
        ))}
        {items.length > 3 && (
          <li className="text-[11px] text-slate-400 px-1">+ {items.length - 3} more</li>
        )}
      </ul>
    </div>
  );
};

export default function ReadinessHub({ tenantId, onNavigate }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['readiness-hub', tenantId],
    queryFn: () => api.get(`/school/workflow/dashboard/${tenantId}`).then((r) => r.data),
    enabled: !!tenantId,
    staleTime: 30_000,
  });

  // Route module/item clicks back to the host page's tab system so admins
  // can jump straight to the failing item.
  const handleClick = (moduleKey, item) => {
    if (!onNavigate) return;
    const map = {
      online_exams_drafts: 'exams',
      quizzes_drafts: 'exams',
      assignments_drafts: 'lms',
      campaigns_drafts: null,  // not in school tabs
    };
    const tab = map[moduleKey];
    if (tab) onNavigate(tab);
  };

  if (isLoading) {
    return (
      <Card className="border-0 shadow-sm" data-testid="readiness-hub-loading">
        <CardContent className="py-6 text-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data?.modules) return null;

  // Overall completion = average of per-module completion (excluding lists).
  const measurable = data.modules.filter((m) => typeof m.completion === 'number');
  const overall = measurable.length
    ? Math.round(measurable.reduce((s, m) => s + (m.completion || 0), 0) / measurable.length)
    : 100;
  const totalPending = data.modules
    .filter((m) => Array.isArray(m.items))
    .reduce((s, m) => s + (m.items.filter((it) => !it.can_publish).length), 0);

  return (
    <Card className="border-0 shadow-sm" data-testid="readiness-hub">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            <Rocket className="h-4 w-4 text-indigo-600" />
            Readiness Hub
          </span>
          <span className="flex items-center gap-2">
            <span className={`text-sm ${pctColor(overall)}`}>{overall}%</span>
            {totalPending > 0 && (
              <Badge variant="outline" className="text-[10px] py-0 px-1.5 text-rose-700 border-rose-200 bg-rose-50">
                {totalPending} blocking
              </Badge>
            )}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {data.modules.map((m) => (
          <ModuleRow key={m.module} module={m} onItemClick={handleClick} />
        ))}
      </CardContent>
    </Card>
  );
}
